import { useEffect } from 'react';
import { useRevalidator } from 'react-router';

/**
 * The Prismic toolbar, plus the two events it fires. Rendered by the root only
 * while the `io.prismic.preview` cookie is set, so production visitors never
 * pay for the script.
 *
 * `revalidate()` re-runs the loaders in place — nicer than a full reload, and
 * enough because a preview request bypasses the Prismic cache entirely.
 */
declare global {
  interface WindowEventMap {
    prismicPreviewUpdate: CustomEvent<{ ref: string }>;
    prismicPreviewEnd: CustomEvent;
  }
}

export function PrismicPreview({ src, nonce }: { src: string; nonce: string }) {
  const { revalidate } = useRevalidator();

  useEffect(() => {
    const onUpdate = () => {
      void revalidate();
    };

    const onEnd = () => {
      void fetch('/api/prismic/exit-preview').then(() => revalidate());
    };

    window.addEventListener('prismicPreviewUpdate', onUpdate);
    window.addEventListener('prismicPreviewEnd', onEnd);

    return () => {
      window.removeEventListener('prismicPreviewUpdate', onUpdate);
      window.removeEventListener('prismicPreviewEnd', onEnd);
    };
  }, [revalidate]);

  return <script async src={src} nonce={nonce} />;
}
